import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_PORTAL_PROJECT_SCOPES, PORTAL_DEFAULT_VISIBILITIES } from '@lims/shared-types';
import { settingsApi, type TenantSettings } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  SelectField,
  SwitchField,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
  useToast,
} from '../../components/ui/index.js';

const portalFromServer = (source: TenantSettings) => ({
  enabled: source.clientPortalEnabled,
  visibility: source.portalDefaultVisibility as string | null,
  scope: source.portalDefaultProjectScope as string | null,
});

/**
 * Client portal settings — Phase 11 (D146).
 *
 * Whether this laboratory offers the portal at all, and the two defaults a
 * new portal account takes when its creation names none of its own. Every
 * value shown is the server's; the backend applies the defaults for a new
 * laboratory (portal OFF) and this page never restates them (CLAUDE.md 6.1).
 *
 * Switching the portal off refuses every portal sign-in and request at once;
 * it deletes no account and touches no record. Saved on its own, like the
 * other settings panels.
 */
export function ClientPortalSettingsPanel({ settings }: { readonly settings: TenantSettings }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const canEdit = can(PERMISSIONS.SETTINGS_UPDATE);

  // Authoritative while edited, reset only by this panel's own success (D205).
  const draft = useServerForm(settings, portalFromServer, portalFromServer(settings));
  const { enabled, visibility, scope } = draft.values;

  const save = useMutation({
    mutationFn: ({ values }: { values: typeof draft.values; mark: number }) =>
      settingsApi.update({
        clientPortalEnabled: values.enabled,
        portalDefaultVisibility: values.visibility ?? settings.portalDefaultVisibility,
        portalDefaultProjectScope: values.scope ?? settings.portalDefaultProjectScope,
      }),
    onSuccess: async (saved, { mark }) => {
      draft.saved(saved, mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const { fields: serverErrors } = useSaveErrors(save.error);
  const panelRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(save.error, panelRef, noticeRef);

  const visibilityOptions = PORTAL_DEFAULT_VISIBILITIES.map((value) => ({
    value,
    label: t(`contacts.portalVisibility.${value}`),
  }));
  const scopeOptions = ALL_PORTAL_PROJECT_SCOPES.map((value) => ({
    value,
    label: t(`portalAccounts.projectScope.${value}`),
  }));

  return (
    <Panel
      title={t('settings.clientPortal.title')}
      description={t('settings.clientPortal.description')}
    >
      <div ref={panelRef} className="flex flex-col gap-gutter">
        {canEdit ? (
          <>
            <SwitchField
              label={t('settings.clientPortal.enabled')}
              description={t('settings.clientPortal.enabledHint')}
              value={enabled}
              onChange={(value) => draft.set('enabled', value)}
            />
            <SelectField
              label={t('settings.clientPortal.defaultVisibility')}
              description={t('settings.clientPortal.defaultVisibilityHint')}
              value={visibility}
              onChange={(value) => draft.set('visibility', value)}
              {...(serverErrors.portalDefaultVisibility
                ? { error: serverErrors.portalDefaultVisibility }
                : {})}
              isRequired
              options={visibilityOptions}
            />
            <SelectField
              label={t('settings.clientPortal.defaultProjectScope')}
              description={t('settings.clientPortal.defaultProjectScopeHint')}
              value={scope}
              onChange={(value) => draft.set('scope', value)}
              {...(serverErrors.portalDefaultProjectScope
                ? { error: serverErrors.portalDefaultProjectScope }
                : {})}
              isRequired
              options={scopeOptions}
            />
          </>
        ) : (
          <>
            <ReadOnlyField label={t('settings.clientPortal.enabled')}>
              {settings.clientPortalEnabled
                ? t('settings.clientPortal.on')
                : t('settings.clientPortal.off')}
            </ReadOnlyField>
            <ReadOnlyField label={t('settings.clientPortal.defaultVisibility')}>
              {t(`contacts.portalVisibility.${settings.portalDefaultVisibility}`)}
            </ReadOnlyField>
            <ReadOnlyField label={t('settings.clientPortal.defaultProjectScope')}>
              {t(`portalAccounts.projectScope.${settings.portalDefaultProjectScope}`)}
            </ReadOnlyField>
          </>
        )}

        {settings.clientPortalEnabled && !enabled ? (
          <Alert
            tone="warning"
            title={t('settings.clientPortal.switchOffTitle')}
            description={t('settings.clientPortal.switchOffBody')}
          />
        ) : null}

        {save.error ? (
          <div ref={noticeRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={save.error} title={t('settings.saveFailed')} />
          </div>
        ) : null}

        {canEdit ? (
          <ActionBar>
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              onPress={() => save.mutate({ values: draft.values, mark: draft.mark() })}
            >
              {t('settings.save')}
            </ActionButton>
          </ActionBar>
        ) : null}
      </div>
    </Panel>
  );
}
